import { Order } from '@interfaces/order';
import { ReceiptState } from '@interfaces/receipts';
import { getPaymentSearchSource } from '@utils/getPaymentSearchSource';
import { checkReceiptIsProcessing } from '@utils/checkReceiptIsProcessing';
import { checkReceiptStateValue } from '@utils/checkReceiptStateValue';
import { ReceiptStateValue } from '@interfaces/receipts';
import {
  getRedirectUrlByOrderStatus,
  getRedirectUrlWithLocale,
  getRedirectUrlWithoutReceiptUpload,
} from '@utils/getRedirectUrlByOrderStatus';
import { isDirectLinkPage } from '@utils/isDirectLinkPage';
import { isReturnPage } from '@utils/isReturnPage';
import { checkReceiptsLimit } from '@utils/checkReceiptsLimit';
import { ROUTES } from '@constants/routes';

/**
 * Determines if a redirect or notFound action is needed based on order status and receipt state
 */
export function getRedirectIfNeeded(
  orderData: Order,
  receiptState: ReceiptState | undefined,
  pathname: string,
  locale: string,
  isReceiptUploadEnabled = true
): { shouldNotFound: boolean; redirectTo: string | null } {
  const normalizedPathname = pathname.startsWith(`/${locale}`)
    ? pathname.slice(locale.length + 1) // Remove /{locale}
    : pathname;

  const source = getPaymentSearchSource(normalizedPathname);
  const onReturnPage = isReturnPage(normalizedPathname);

  if (!isReceiptUploadEnabled) {
    const redirectUrl = getRedirectUrlWithoutReceiptUpload(
      orderData,
      onReturnPage,
      isDirectLinkPage(normalizedPathname) ||
        normalizedPathname === ROUTES.transactionSendReceipt(orderData.id)
    );

    if (redirectUrl && normalizedPathname !== redirectUrl && orderData.id) {
      return {
        shouldNotFound: false,
        redirectTo: getRedirectUrlWithLocale(redirectUrl, locale),
      };
    }

    return { shouldNotFound: false, redirectTo: null };
  }

  const isReceiptProcessing = checkReceiptIsProcessing(source, receiptState);
  const isReceiptDisabled = checkReceiptStateValue(source, ReceiptStateValue.disabled, receiptState);
  const isReceiptsLimit = !checkReceiptsLimit({ source, receiptState });
  const redirectUrl = getRedirectUrlByOrderStatus(
    orderData,
    isReceiptProcessing,
    isReceiptsLimit,
    isReceiptDisabled,
    onReturnPage
  );
  // Check if should trigger notFound
  if (isDirectLinkPage(normalizedPathname) && isReceiptDisabled) {
    return { shouldNotFound: true, redirectTo: null };
  }

  // Check if should redirect
  if (
    redirectUrl &&
    normalizedPathname !== redirectUrl &&
    orderData.id &&
    !isDirectLinkPage(normalizedPathname)
  ) {
    return {
      shouldNotFound: false,
      redirectTo: getRedirectUrlWithLocale(redirectUrl, locale),
    };
  }

  return { shouldNotFound: false, redirectTo: null };
}
